import type { FunctionComponent, PropsWithChildren } from 'react'
import React, { useEffect, useMemo } from 'react'

import { AppError } from '@app/errors/AppError'

import { chunkErrorAfterReload } from '@app/utils/errorReport/chunkLoadError'
import { reloadForChunkError, shouldReloadForChunkError } from '@app/utils/errorReport/chunkLoadErrorReload'
import { captureException } from '@app/utils/errorReport/errorReport'
import { generateSentryErrorId } from '@app/utils/generateSentryErrorId'

import { NotFoundError } from '@app/packages/routing/NotFoundError'
import type { History } from '@app/packages/routing/types'

import { useMountEffect } from '@app/hooks/useMountEffect'

import type { FallbackRender } from '@app/components/ErrorBoundary'
import { ErrorBoundary } from '@app/components/ErrorBoundary'
import { ErrorPage } from '@app/components/ErrorPage/ErrorPage'

export const AppErrorBoundary: FunctionComponent<PropsWithChildren<{ history?: History }>> = ({ history, children }) => {
  return (
    <ErrorBoundary ctx={history} fallback={Fallback}>
      {children}
    </ErrorBoundary>
  )
}

const Fallback: FallbackRender<History | undefined> = ({ error: pError, componentStack, ctx: history, reset }) => {
  const error = useMemo(
    () => (pError instanceof NotFoundError ? new AppError('Page not found', { cause: pError }).withStatus(404).withHandled(true).withExpose(true) : pError),
    [pError]
  )
  const errorID = useMemo(() => generateSentryErrorId(), [])

  useMountEffect(() => {
    const unlisten = history?.listen(() => {
      reset()
    })
    return () => {
      unlisten?.()
    }
  })

  // The router awaits the route's chunk itself and rethrows what it caught, so a chunk that will
  // not load arrives here rather than at the window listeners. A reload is the answer to it; one
  // that reaches here anyway has already had its reload, and is a real error.
  useEffect(() => {
    if (shouldReloadForChunkError(error)) {
      reloadForChunkError()
      return
    }
    captureException(chunkErrorAfterReload(error), { silent: true, tags: { error_uuid: errorID } })
  }, [error, errorID])

  return <ErrorPage error={error} errorID={errorID} info={componentStack} />
}
